import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faUser } from "@fortawesome/free-solid-svg-icons";
// Removed react-icons/fa imports for amenities; now using PNGs
import { countryCodes } from "../../data/countries";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

function BookingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { bookingData } = location.state || {};

  // Amenity icon mapping - use PNGs for consistency with email
  const amenityIcons = {
    WiFi: "/images/wifi.png",
    Pool: "/images/pool.png",
    Spa: "/images/spa.png",
    Restaurant: "/images/restaurant.png",
    Parking: "/images/Parking.png",
    "Nearby Parking": "/images/nearby-parking.png",
    Bar: "/images/bar.png",
    Breakfast: "/images/breakfast1.png",
    "Luggage-service": "/images/luggage-service.png",
  };

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    countryCode: "+1",
    phoneNumber: "",
    specialRequest: "",
  });

  const [showSpecialRequest, setShowSpecialRequest] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const dropdownRef = useRef(null);
  const [isTermsChecked, setIsTermsChecked] = useState(false);
  const [error, setError] = useState("");
  const { t } = useTranslation();

  const filteredCountries = countryCodes.filter(
    (country) =>
      country.country.toLowerCase().includes(countrySearch.toLowerCase()) ||
      country.code.includes(countrySearch)
  );
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Get event genre for the header text
  const getGenreText = () => {
    if (!bookingData?.event?.genre) return "Opera";
    const genre = bookingData.event.genre.toLowerCase();
    if (genre.includes("opera")) return "Opera";
    if (genre.includes("concert")) return "Concert";
    if (genre.includes("ballet")) return "Ballet";
    return "Opera";
  };

  // Get selected country data
  const getSelectedCountry = () => {
    // For +1, prioritize United States over Canada
    if (formData.countryCode === "+1") {
      return (
        countryCodes.find(
          (c) => c.code === "+1" && c.country === "United States"
        ) || countryCodes.find((c) => c.code === formData.countryCode)
      );
    }
    return (
      countryCodes.find((c) => c.code === formData.countryCode) ||
      countryCodes[0]
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const response = await axios.post("api/requests", {
        eventId: bookingData.event._id,
        eventDates:
          bookingData.isCustomInquiry && bookingData.hasBasePrice
            ? [
              new Date(bookingData.inquiryForm.customStartDate).toISOString(),
              new Date(bookingData.inquiryForm.customEndDate).toISOString(),
            ]
            : bookingData.inquiryForm.eventDates,
        seatingPreference: bookingData.inquiryForm.seatingPreference,
        numberOfGuests: bookingData.inquiryForm.numberOfGuests,
        hotelPreferences: bookingData.inquiryForm.hotelPreference,
        tripDuration: bookingData.inquiryForm.tripDuration,
        customerName: `${formData.firstName} ${formData.lastName}`,
        selectedRooms: bookingData.selectedRooms,
        email: formData.email,
        phone: `${formData.countryCode}${formData.phoneNumber}`,
        additionalGuestNames: "",
        hotels: bookingData.hotels || [],
        isCustomInquiry: bookingData.isCustomInquiry,
        specialRequest: formData.specialRequest,
      });
      if (response.status === 200 || response.status === 201) {
        toast.success(t('BookingPage.bookingRequestSuccess'));

        const updatedBookingData = {
          ...bookingData,
          customerName: `${formData.firstName} ${formData.lastName}`,
        };

        navigate("/thank-you", {
          state: {
            bookingData: updatedBookingData,
            requestId: response.data.requestId,
          },
        });
      } else {
        toast.error(t('BookingPage.bookingRequestFailed'));
      }
    } catch (error) {
      toast.error(
        t('BookingPage.bookingError')
      );
    }
  };

  if (!bookingData) {
    return (
      <div className="min-h-screen pt-[80px] bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl text-gray-800 mb-4">
            {t('BookingPage.bookingSessionExpired')}
          </h1>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 bg-opera-red text-white rounded-md hover:bg-opera-hover"
          >
            {t('BookingPage.returnToHome')}
          </button>
        </div>
      </div>
    );
  }

  const imageUrl = bookingData.event.venue.coverImage || bookingData.event.venue.operaImage;
  const encodedUrl = imageUrl ? encodeURI(imageUrl) : '';
  return (
    <div className="min-h-screen pt-16 md:pt-32 lg:pt-[180px] bg-gray-50 ">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-3  space-y-6 order-2 lg:order-1">
            {/* Header Box */}
            <div
              className="relative bg-black text-white p-6 rounded-lg"
              style={{
                backgroundImage: `url(${encodedUrl})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg"></div>
              <div className="relative z-10">
                <h1 className="text-2xl text-white">
                  {t('BookingPage.enjoyThe')} {getGenreText()} - {t('BookingPage.itsAllTakenCareOf')}
                </h1>
              </div>
            </div>

            {/* Booking Form */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <h2 className="text-xl font-normal text-gray-800">
                  {t('BookingPage.whoShallWeReserve')}{", "}
                  {t('BookingPage.journeyFor')}
                </h2>
                {/* Name Section */}
                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">
                    {t('BookingPage.name')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('BookingPage.firstName')} *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-400 focus:border-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('BookingPage.lastName')} *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-400 focus:border-gray-400"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-4">
                    {t('BookingPage.contactInformation')}
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('BookingPage.emailAddress')} *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-400 focus:border-gray-400"
                      />
                    </div>

                    {/* Country/Region and Phone */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('BookingPage.countryRegion')}
                        </label>
                        <div className="relative" ref={dropdownRef}>
                          {/* Country Code Button */}
                          <button
                            type="button"
                            onClick={() =>
                              setShowCountryDropdown(!showCountryDropdown)
                            }
                            className="w-full inline-flex items-center justify-between py-3 px-4 text-sm font-medium text-gray-900 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-1 focus:outline-none focus:ring-gray-400 focus:border-gray-400"
                          >
                            <span className="flex items-center">
                              <span className="mr-2">
                                {getSelectedCountry().flag}
                              </span>
                              {getSelectedCountry().code}
                            </span>
                            <FontAwesomeIcon
                              icon={faChevronDown}
                              className={`w-2.5 h-2.5 transform transition-transform ${showCountryDropdown ? "rotate-180" : ""
                                }`}
                            />
                          </button>

                          {/* Country Dropdown */}
                          {showCountryDropdown && (
                            <div className="absolute top-full left-0 z-20 mt-1 bg-white divide-y divide-gray-100 rounded-lg shadow-lg w-72 max-h-60 overflow-y-auto border border-gray-200">
                              {/* Search input */}
                              <div className="p-2">
                                <input
                                  type="text"
                                  value={countrySearch}
                                  onChange={(e) =>
                                    setCountrySearch(e.target.value)
                                  }
                                  placeholder={t('BookingPage.searchCountryOrCode')}
                                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-400"
                                  autoFocus
                                />
                              </div>
                              <ul className="py-2 text-sm text-gray-700 max-h-40 overflow-y-auto">
                                {filteredCountries.length === 0 && (
                                  <li className="px-4 py-2 text-gray-400">
                                    {t('BookingPage.NoResultsFound')}
                                  </li>
                                )}
                                {filteredCountries.map((country) => (
                                  <li
                                    key={`${country.code}-${country.country}`}
                                  >
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setFormData((prev) => ({
                                          ...prev,
                                          countryCode: country.code,
                                        }));
                                        setShowCountryDropdown(false);
                                        setCountrySearch(""); // reset search
                                      }}
                                      className="inline-flex w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:bg-gray-50"
                                    >
                                      <span className="inline-flex items-center">
                                        <span className="mr-3 text-lg">
                                          {country.flag}
                                        </span>
                                        {country.country} ({country.code})
                                      </span>
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('BookingPage.phoneNumber')}
                        </label>
                        <input
                          type="tel"
                          name="phoneNumber"
                          value={formData.phoneNumber}
                          onChange={handleInputChange}
                          placeholder="123-456-7890"
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-1 focus:ring-gray-400 focus:border-gray-400"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Special Request */}
                <div>
                  <button
                    type="button"
                    onClick={() => setShowSpecialRequest(!showSpecialRequest)}
                    className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                  >
                    {t('BookingPage.specialRequestsOptional')}
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className={`transform transition-transform ${showSpecialRequest ? "rotate-180" : ""
                        }`}
                    />
                  </button>

                  {showSpecialRequest && (
                    <div className="mt-3">
                      <textarea
                        name="specialRequest"
                        value={formData.specialRequest}
                        onChange={handleInputChange}
                        rows={4}
                        placeholder="Please let us know if you have any special requests..."
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-400 focus:border-gray-400 resize-vertical"
                      />
                    </div>
                  )}
                </div>
                {/* <div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="terms"
                      checked={isTermsChecked}
                      onChange={(e) => setIsTermsChecked(e.target.checked)}
                      className="h-4 w-4"
                    />
                    <p>{t('BookingPage.iConfirmReadAndAccept')}<a href='/terms-conditions' className="text-opera-red underline">{t('BookingPage.termsAndConditions')}</a></p>
                  </div>
                  {error && !isTermsChecked && <p className="text-opera-red text-sm">{error}</p>}
                </div> */}
                {/* Book Now Button */}
                <button
                  type="submit"
                  className="w-full py-4 bg-opera-red text-white text-lg font-medium rounded-md hover:bg-opera-hover transition-colors"
                >
                  {bookingData.isCustomInquiry ? t('EventPage.sendRequest') : t('EventPage.bookNow')}
                </button>
              </form>
            </div>

            {/* Venue Reviews Section */}
            <VenueReviewsComponent venueId={bookingData?.event?.venue?._id} />

            {/* Secure Booking & Financial Protection Section */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3
                className="text-lg font-medium text-gray-800 mb-4"
                style={{ fontFamily: "Lato, sans-serif", fontWeight: "400" }}
              >
                {t('BookingPage.secureBookingFinancialProtection')}
              </h3>

              <div className="space-y-4">
                {/* EU Protection */}
                <div className="flex items-start">
                  <img
                    src="/images/Europe.jpg"
                    alt="European Union"
                    className="w-12 h-8 mr-4 mt-1 flex-shrink-0 object-contain"
                  />
                  <div>
                    <p
                      className="text-sm text-gray-700 leading-relaxed"
                      style={{
                        fontFamily: "Lato, sans-serif",
                        fontWeight: "400",
                      }}
                    >
                      {t('BookingPage.paymentsProcessedSecurely')}
                    </p>
                  </div>
                </div>

                {/* R+V Insurance */}
                <div className="flex items-start">
                  <img
                    src="/images/r+v.jpg"
                    alt="R+V Insurance"
                    className="w-12 h-8 mr-4 mt-1 flex-shrink-0 object-contain"
                  />
                  <div>
                    <p
                      className="text-sm text-gray-700 leading-relaxed"
                      style={{
                        fontFamily: "Lato, sans-serif",
                        fontWeight: "400",
                      }}
                    >
                      {t('BookingPage.allPaymentsProtected')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="lg:col-span-2 space-y-6 order-1 lg:order-2">
            {/* First Section - Event Details */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              {/* Venue Cover Picture */}
              <div className="h-64 bg-gray-200 overflow-hidden rounded-t-3xl">
                {bookingData?.event?.venue?.coverImage ||
                  bookingData?.event?.venue?.operaImage ? (
                  <img
                    src={
                      bookingData.event.venue.coverImage ||
                      bookingData.event.venue.operaImage
                    }
                    alt={bookingData.event.venue.name || "Venue"}
                    className="w-full h-full object-cover rounded-t-3xl"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-t-3xl">
                    <span className="text-gray-400 text-sm"> {t('BookingPage.venueImage')}</span>
                  </div>
                )}
              </div>

              {/* Venue and Event Details */}
              <div className="p-6">
                {/* Venue Section */}
                <div>
                  <h4
                    className="text-2xl mb-1 capitalize"
                  >
                    {bookingData?.event?.venue?.name || t('BookingPage.venueName')}
                  </h4>

                </div>
                <div className="flex items-center mb-6">
                  <img
                    src="/images/tickets.jpg"
                    alt="Venue"
                    className="w-8 h-8 mr-3 mt-1 flex-shrink-0"
                  />
                  <p
                    className="text-sm text-gray-600"
                    style={{
                      fontFamily: "Lato, sans-serif",
                      fontWeight: "400",
                    }}
                  >
                    {[
                      bookingData?.event?.venue?.streetAddress,
                      bookingData?.event?.venue?.city,
                      bookingData?.event?.venue?.country,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                </div>

                {/* Event Section */}
                <div className="mb-6">
                  <h4
                    className="text-2xl mb-2 capitalize"
                  >
                    {bookingData?.event?.title || "Event Title"}
                  </h4>
                  <p
                    className="text-sm text-gray-600 mb-3"
                    style={{
                      fontFamily: "Lato, sans-serif",
                      fontWeight: "400",
                    }}
                  >
                    {(() => {
                      // If custom inquiry, use customStartDate and customEndDate
                      if (
                        bookingData.isCustomInquiry &&
                        bookingData.hasBasePrice
                      ) {
                        const startDate =
                          bookingData?.inquiryForm?.customStartDate;
                        const endDate = bookingData?.inquiryForm?.customEndDate;

                        if (startDate && endDate) {
                          const start = new Date(startDate);
                          const end = new Date(endDate);

                          const options = {
                            weekday: "long",
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          };

                          const formattedStart = start.toLocaleDateString(
                            "en-US",
                            options
                          );
                          const formattedEnd = end.toLocaleDateString(
                            "en-US",
                            options
                          );

                          // Optionally, add time if you have it
                          // const startTime = start.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
                          // const endTime = end.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });

                          return `${formattedStart} to ${formattedEnd}`;
                        }
                        return "Dates not available";
                      }

                      // Regular event date logic
                      const eventDate =
                        bookingData?.inquiryForm?.eventDates?.[0];
                      if (eventDate) {
                        const date = new Date(eventDate);
                        const options = {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        };
                        const formattedDate = date.toLocaleDateString(
                          "en-US",
                          options
                        );

                        // Find the matching date_time entry to get the time
                        const selectedDateTime =
                          bookingData?.event?.date_times?.find(
                            (dt) =>
                              new Date(dt.date).toDateString() ===
                              date.toDateString()
                          );

                        if (selectedDateTime) {
                          const fullDateTime = new Date(selectedDateTime.date);
                          const timeString = fullDateTime.toLocaleTimeString(
                            "en-US",
                            {
                              hour: "numeric",
                              minute: "2-digit",
                              hour12: true,
                            }
                          );
                          return `${formattedDate} at ${timeString}`;
                        } else {
                          return formattedDate;
                        }
                      }
                      return t('BookingPage.DateNotAvailable');
                    })()}
                  </p>

                  {/* Seating Section with tickets0.jpg icon */}
                  <div className="flex items-center mb-3">
                    <img
                      src="/images/tickets0.jpg"
                      alt="Seating"
                      className="w-8 h-8 mr-3 flex-shrink-0"
                    />
                    <span
                      className="text-sm text-gray-600"
                      style={{
                        fontFamily: "Lato, sans-serif",
                        fontWeight: "400",
                      }}
                    >
                      {bookingData?.inquiryForm?.seatingPreference ||
                        t('BookingPage.notSelected')}
                    </span>
                    {bookingData?.event?.venue?.seatingPlanDescription && (
                      <div className="relative ml-2 group">
                        <div className="w-4 h-4 bg-gray-400 rounded-full flex items-center justify-center cursor-pointer hover:bg-gray-500">
                          <span className="text-white text-xs font-bold">
                            i
                          </span>
                        </div>
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 p-2 bg-black text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-48 z-10">
                          {bookingData.event.venue.seatingPlanDescription}
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-black"></div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Fine line separator */}
                <hr className="border-gray-200 my-4" />

                {/* Hotel Section */}
                {bookingData?.inquiryForm?.hotelPreference?.length > 0 && (
                  <div className="mb-6">
                    {(() => {
                      const hotelPref =
                        bookingData.inquiryForm.hotelPreference[0];
                      // Try to find the hotel from the hotels array if available
                      const hotel = bookingData?.hotels?.find(
                        (h) => h._id === hotelPref.hotelId
                      );

                      return (
                        <div className="flex items-start mb-3">
                          <img
                            src="/images/hotelbig.jpg"
                            alt="Hotel"
                            className="w-8 h-8 mr-3 mt-1 flex-shrink-0"
                          />
                          <div>
                            <h5 className="text-base font-medium text-opera-red mb-1">
                              {/* Prioritize hotel name from preference, fallback to hotel object */}
                              {hotelPref?.hotelName ||
                                hotel?.name ||
                                t('BookingPage.hotelName')}
                              {/* Display stars if hotel data is available */}
                              {hotel?.starRating && (
                                <span className="ml-2 text-yellow-500">
                                  {"★".repeat(hotel.starRating)}
                                </span>
                              )}
                            </h5>
                            <p
                              className="text-sm text-gray-600"
                              style={{
                                fontFamily: "Lato, sans-serif",
                                fontWeight: "300",
                              }}
                            >
                              {/* Display address if hotel data is available, otherwise generic text */}
                              {hotel?.address || "Hotel Address"}
                            </p>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Selected Room */}
                    {bookingData?.selectedRooms?.length > 0 && (
                      <div className="flex items-center mb-2">
                        <img
                          src="/images/hotel.jpg"
                          alt="Room"
                          className="w-8 h-8 mr-3 flex-shrink-0"
                        />
                        <span
                          className="text-sm text-gray-600"
                          style={{
                            fontFamily: "Lato, sans-serif",
                            fontWeight: "400",
                          }}
                        >
                          {bookingData.selectedRooms
                            .map((room) => {
                              const roomType = room.type || room.roomType;
                              const quantity = room.quantity || 1;
                              // Only show quantity if greater than 1
                              return quantity > 1
                                ? `${roomType} (Qty: ${quantity})`
                                : roomType;
                            })
                            .join(", ")}
                        </span>
                      </div>
                    )}

                    {/* Breakfast */}
                    <div className="flex items-center mb-3">
                      <img
                        src="/images/breakfast.jpg"
                        alt="Breakfast"
                        className="w-8 h-8 mr-3 flex-shrink-0"
                      />
                      <span
                        className="text-sm text-gray-600"
                        style={{
                          fontFamily: "Lato, sans-serif",
                          fontWeight: "400",
                        }}
                      >
                        {t('BookingPage.breakfastIncluded')}
                      </span>
                    </div>

                    {/* Check-in/Check-out dates */}
                    <div className="flex items-start">
                      <img
                        src="/images/calendar.jpg"
                        alt="Calendar"
                        className="w-8 h-8 mr-3 mt-1 flex-shrink-0"
                      />
                      <div>
                        <div className="mb-2">
                          <p
                            className="text-sm text-gray-600"
                            style={{
                              fontFamily: "Lato, sans-serif",
                              fontWeight: "300",
                            }}
                          >
                            {t('BookingPage.checkIn')}
                          </p>
                          <p
                            className="text-sm text-gray-800"
                            style={{
                              fontFamily: "Lato, sans-serif",
                              fontWeight: "400",
                            }}
                          >
                            {(() => {
                              const startDate =
                                bookingData?.inquiryForm?.tripDuration
                                  ?.startDate;
                              if (startDate) {
                                const date = new Date(startDate);
                                const dayName = date.toLocaleDateString(
                                  "en-US",
                                  { weekday: "long" }
                                );
                                const formattedDate = date.toLocaleDateString(
                                  "en-US",
                                  {
                                    month: "long",
                                    day: "numeric",
                                    year: "numeric",
                                  }
                                );
                                return `${dayName}, ${formattedDate}`;
                              }
                              return "Check-in date";
                            })()}
                          </p>
                        </div>
                        <div>
                          <p
                            className="text-sm text-gray-600"
                            style={{
                              fontFamily: "Lato, sans-serif",
                              fontWeight: "300",
                            }}
                          >
                            {t('BookingPage.checkOut')}
                          </p>
                          <p
                            className="text-sm text-gray-800"
                            style={{
                              fontFamily: "Lato, sans-serif",
                              fontWeight: "400",
                            }}
                          >
                            {(() => {
                              const endDate =
                                bookingData?.inquiryForm?.tripDuration?.endDate;
                              if (endDate) {
                                const date = new Date(endDate);
                                const dayName = date.toLocaleDateString(
                                  "en-US",
                                  { weekday: "long" }
                                );
                                const formattedDate = date.toLocaleDateString(
                                  "en-US",
                                  {
                                    month: "long",
                                    day: "numeric",
                                    year: "numeric",
                                  }
                                );
                                return `${dayName}, ${formattedDate}`;
                              }
                              return t('BookingPage.checkOutDate');
                            })()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Property Highlights Section */}
                <div className="mt-6">
                  <hr className="border-gray-200 mb-4" />
                  <h4 className="text-sm font-medium text-gray-800 mb-3">
                    {t('BookingPage.propertyHighlights')}
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    {(() => {
                      const hotelPref =
                        bookingData?.inquiryForm?.hotelPreference?.[0];
                      const hotel = bookingData?.hotels?.find(
                        (h) => h._id === hotelPref?.hotelId
                      );
                      const amenities = hotel?.amenities || [];

                      // Display hotel amenities, or show fallback message if no amenities
                      if (amenities.length === 0) {
                        return (
                          <div className="col-span-2 text-sm text-gray-500">
                            {t('BookingPage.noAmenitiesAvailable')}
                          </div>
                        );
                      }

                      return amenities.map((amenity, index) => {
                        const iconUrl = amenityIcons[amenity];
                        return (
                          <div key={index} className="flex items-center">
                            {iconUrl ? (
                              <img
                                src={iconUrl}
                                alt={amenity}
                                className="w-5 h-5 mr-2 object-contain grayscale"
                                style={{ minWidth: 40, minHeight: 40 }}
                              />
                            ) : (
                              <div className="w-5 h-5 mr-2 flex-shrink-0" />
                            )}
                            <span className="text-sm text-gray-600 align-middle">
                              {amenity}
                            </span>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>
            </div>

            {/* Second Section - Price Details */}
            {!bookingData.isCustomInquiry && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3
                  className="text-lg font-medium text-gray-800 mb-4"
                  style={{ fontFamily: "Lato, sans-serif", fontWeight: "400" }}
                >
                  {t('BookingPage.priceDetails')}
                </h3>

                {/* Price per person */}
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-gray-600">
                    {t('EventPage.pricePerPerson')}
                  </span>
                  <span className="text-sm text-gray-800">
                    €
                    {bookingData?.hotelPricesPerPerson?.toLocaleString(
                      "en-US",
                      { minimumFractionDigits: 0, maximumFractionDigits: 0 }
                    ) || "0"}
                  </span>
                </div>

                {/* Number of guests */}
                <div className="flex items-center mb-3">
                  <FontAwesomeIcon
                    icon={faUser}
                    className="text-gray-600 mr-2"
                  />
                  <span className="text-sm text-gray-600 mr-2">
                    {bookingData?.inquiryForm?.numberOfGuests || 1} {t('BookingPage.guests')}
                  </span>
                </div>

                {/* Taxes & fees */}
                <div className="flex justify-between items-center mb-3">
                  <div className="flex items-center">
                    <span
                      className="text-xs text-gray-600 mr-2"
                      style={{
                        fontFamily: "Lato, sans-serif",
                        fontWeight: "300",
                      }}
                    >
                      {t('BookingPage.taxesFees')}
                    </span>
                    <div className="relative group">
                      <div className="w-4 h-4 bg-gray-400 rounded-full flex items-center justify-center cursor-pointer hover:bg-gray-500">
                        <span className="text-white text-xs font-bold">i</span>
                      </div>
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 p-2 bg-black text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-48 z-10">
                        {t('BookingPage.allTaxesIncluded')}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-black"></div>
                      </div>
                    </div>
                  </div>
                  <span className="text-sm text-gray-800">{t('BookingPage.Included')}</span>
                </div>

                {/* Partially refundable */}
                <div className="flex items-center mb-4">
                  <span className="text-sm text-gray-600 mr-2">
                    {t('BookingPage.partiallyRefundable')}
                  </span>
                  <div className="relative group">
                    <div className="w-4 h-4 bg-gray-400 rounded-full flex items-center justify-center cursor-pointer hover:bg-gray-500">
                      <span className="text-white text-xs font-bold">i</span>
                    </div>
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 p-2 bg-black text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-48 z-10">
                      {t('BookingPage.operaTicketsNonRefundable')}
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-black"></div>
                    </div>
                  </div>
                </div>

                {/* Total */}
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between items-center mb-4">
                    <span
                      className="text-lg font-medium text-gray-800"
                      style={{
                        fontFamily: "Lato, sans-serif",
                        fontWeight: "400",
                      }}
                    >
                      {t('BookingPage.total')}:
                    </span>
                    <span className="text-lg font-medium text-gray-800">
                      €
                      {bookingData?.totalPrice?.toLocaleString("en-US", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }) || "0"}
                    </span>
                  </div>
                </div>

                {/* Footer text */}
                <div className="text-xs text-gray-500 mb-4">
                  <p className="mb-2">{t('BookingPage.ratesInEUR')} (€).</p>
                  <p>
                    {t('BookingPage.tourOperatedBy')}
                  </p>
                </div>

                {/* Price increase notice */}
                <div className="flex items-center text-sm text-gray-600">
                  <img
                    src="/images/arrow-growth.svg"
                    alt="Price increase"
                    className="mr-2 w-4 h-4"
                  />
                  <span>{t('BookingPage.priceMayIncrease')}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Venue Reviews Component

function VenueReviewsComponent({ venueId }) {
  const [reviews, setReviews] = useState([]);
  const [randomIndex, setRandomIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  useEffect(() => {
    const fetchReviews = async () => {
      if (!venueId) return;

      try {
        const response = await axios.get("/api/information/public/reviews");

        // Handle different possible response formats
        let reviewsData = response.data;
        if (
          reviewsData &&
          reviewsData.entries &&
          Array.isArray(reviewsData.entries)
        ) {
          reviewsData = reviewsData.entries;
        }

        // Filter reviews for this specific venue
        const venueReviews = reviewsData.filter(
          (review) => typeof review === "object" && review.venue === venueId
        );

        setReviews(venueReviews);
        // Pick a random index if there are reviews
        if (venueReviews.length > 0) {
          setRandomIndex(Math.floor(Math.random() * venueReviews.length));
        } else {
          setRandomIndex(null);
        }
      } catch (error) {
        console.error("Error fetching venue reviews:", error);
        setReviews([]);
        setRandomIndex(null);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, [venueId]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-3 bg-gray-200 rounded mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  if (!reviews.length || randomIndex === null) {
    return null; // Don't show the component if no reviews for this venue
  }

  const currentReview = reviews[randomIndex];

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center mb-4">
        <div className="w-12 h-12 flex items-center justify-center mr-3">
          <img
            src="/images/reviews.png"
            alt="Reviews"
            className="w-12 h-12 object-contain"
          />
        </div>
        <h3
          className="text-lg font-medium text-gray-800"
          style={{ fontFamily: "Lato, sans-serif", fontWeight: "400" }}
        >
          {currentReview.name || "Anonymous"} |
          {currentReview.date
            ? ` ${new Date(currentReview.date).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}`
            : " Date"}
        </h3>
      </div>

      <div className="mb-4">
        <p className="text-gray-800 text-medium leading-relaxed whitespace-pre-wrap  mb-3">
          {currentReview.text}
        </p>

        {/* Old name and date removed; now shown in header */}
      </div>

      {/* Fine line separator */}
      <hr className="border-gray-200 my-4" />

      {/* Call to action text */}
      <p className="text-gray-700 text-sm leading-relaxed">
        {t('BookingPage.guestReviewsDiscover')}{" "}
        <a
          href="/testimonials"
          className="text-opera-red underline hover:text-opera-hover transition-colors"
        >
          {t('BookingPage.reviewsAndTravelHighlights')}
        </a>{" "}
        {t('BookingPage.fromOperaTodayTravelers')}
      </p>
    </div>
  );
}

export default BookingPage;
